//>>> Environment configuration with domain-aware API endpoints
import { detectDomain } from '@utils/domain';

//>>> Base API URLs from environment
const OWNER_API_BASE = import.meta.env.VITE_OWNER_API_URL || 'http://localhost:4001/api/v1';
const TENANT_API_BASE = import.meta.env.VITE_TENANT_API_URL || 'http://localhost:4002/api/v1';

const currentDomain = detectDomain();

const getApiUrl = () => {
    if (currentDomain === 'owner') {
        return `${OWNER_API_BASE}`;
    }
    return `${TENANT_API_BASE}`;
};

//>>> Domain-aware API endpoints
const getOwnerApiUrl = () => {
    return `${OWNER_API_BASE}`;
};

const getTenantApiUrl = () => {
    return `${TENANT_API_BASE}`;
};

/** Default production Firebase credentials (used as safety fallback if build env is empty) */
const DEFAULT_FIREBASE_PROD = {
    apiKey: 'AIzaSyD4IBFPdqedDwliSQ0tf1Q0luGM7I3yzrs',
    authDomain: 'school-crm-15329.firebaseapp.com',
    projectId: 'school-crm-15329',
    storageBucket: 'school-crm-15329.firebasestorage.app',
    messagingSenderId: '492846962193',
    appId: '1:492846962193:web:445f234a5c5a61630803b0',
};

/** Vite mode: `development` → *_DEV Firebase keys; `production` → production keys (see package.json scripts). */
const useProductionFirebase = import.meta.env.MODE === 'production';

const firebaseConfig = useProductionFirebase
    ? {
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY || DEFAULT_FIREBASE_PROD.apiKey,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || DEFAULT_FIREBASE_PROD.authDomain,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || DEFAULT_FIREBASE_PROD.projectId,
          storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || DEFAULT_FIREBASE_PROD.storageBucket,
          messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || DEFAULT_FIREBASE_PROD.messagingSenderId,
          appId: import.meta.env.VITE_FIREBASE_APP_ID || DEFAULT_FIREBASE_PROD.appId,
          CREATE_TENANT_ADMIN_FUNCTION_NAME: import.meta.env.VITE_CREATE_TENANT_ADMIN_FUNCTION_NAME || '',
          SEND_FEE_INVOICE_FUNCTION_NAME: import.meta.env.VITE_SEND_FEE_INVOICE_FUNCTION_NAME || '',
          SEND_NOTICE_PUSH_FUNCTION_NAME: import.meta.env.VITE_SEND_NOTICE_PUSH_FUNCTION_NAME || '',
      }
    : {
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY_DEV || import.meta.env.VITE_FIREBASE_API_KEY || DEFAULT_FIREBASE_PROD.apiKey,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN_DEV || import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || DEFAULT_FIREBASE_PROD.authDomain,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID_DEV || import.meta.env.VITE_FIREBASE_PROJECT_ID || DEFAULT_FIREBASE_PROD.projectId,
          storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET_DEV || import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || DEFAULT_FIREBASE_PROD.storageBucket,
          messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID_DEV || import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || DEFAULT_FIREBASE_PROD.messagingSenderId,
          appId: import.meta.env.VITE_FIREBASE_APP_ID_DEV || import.meta.env.VITE_FIREBASE_APP_ID || DEFAULT_FIREBASE_PROD.appId,
          CREATE_TENANT_ADMIN_FUNCTION_NAME: import.meta.env.VITE_CREATE_TENANT_ADMIN_FUNCTION_NAME || '',
          SEND_FEE_INVOICE_FUNCTION_NAME: import.meta.env.VITE_SEND_FEE_INVOICE_FUNCTION_NAME || '',
          SEND_NOTICE_PUSH_FUNCTION_NAME: import.meta.env.VITE_SEND_NOTICE_PUSH_FUNCTION_NAME || '',
      };

const cloudinaryConfig = {
    cloudName: import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || '',
    uploadPreset: import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET || '',
    // Optional: if you later add a backend/callable to delete assets securely.
    deleteFunctionName: import.meta.env.VITE_CLOUDINARY_DELETE_FUNCTION_NAME || '',
};

const ENV = {
    APP_NAME: import.meta.env.VITE_APP_NAME || 'School CRM',

    /** Firebase web config (Vite env). */
    FIREBASE: firebaseConfig,

    CLOUDINARY: cloudinaryConfig,

    WHATSAPP_BACKEND: {
        SEND_INVOICE_URL: import.meta.env.VITE_WHATSAPP_SEND_INVOICE_URL || 'http://localhost/api/send_invoice.php',
        API_KEY: import.meta.env.VITE_WHATSAPP_BACKEND_API_KEY || '',
    },
    NOTICE_PUSH_BACKEND: {
        SEND_PUSH_URL: import.meta.env.VITE_NOTICE_PUSH_URL || '',
        API_KEY: import.meta.env.VITE_NOTICE_PUSH_BACKEND_API_KEY || '',
    },
    
    // Domain detection
    DOMAIN: currentDomain,
    IS_OWNER: currentDomain === 'owner',
    IS_TENANT: currentDomain === 'tenant',
    
    // Current API URL (domain-aware)
    API_URL: getApiUrl(),
    
    // Specific API URLs
    OWNER_API_URL: getOwnerApiUrl(),
    TENANT_API_URL: getTenantApiUrl(),
    
    // Legacy support (will use domain-aware API_URL)
    API_URL_STREAM: import.meta.env.VITE_API_URL_STREAM || '',
    FRONT_FACING_URL: import.meta.env.VITE_FRONT_FACING_URL || '',
    BASE_URL: import.meta.env.VITE_BASE_URL || '',
    
    // Environment
    NODE_ENV: import.meta.env.NODE_ENV || 'development',
    PORT: import.meta.env.PORT || 3000,
    HOST: import.meta.env.HOST || '0.0.0.0',
    SSL: import.meta.env.SSL || false,
    SSL_KEY: import.meta.env.SSL_KEY || '',
    SSE_RETRY_MS: import.meta.env.VITE_SSE_RETRY_MS || 3000
};

export { ENV };
